
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudForm from "../../../components/dashboard/form/CrudForm";
import { useCrud } from "../../../hooks/useCrud";

import {
 
  validateBannerTitle,
  validateBannerContent,
  validateStartingPrice,
  validateBannerImage,
} from "../../../utils/Validations";

export default function CreateBanner() {
  const navigate = useNavigate();
  const { createItem } = useCrud("backend/banners", ["banner"]);
  const [loading, setLoading] = useState(false);

  const allowedFields = [
    "banner",
    "banner_title",
    "banner_content",
    "starting_price",
    "status",
  ];

  const initialData = allowedFields.reduce((acc, field) => {
    acc[field] = field === "status" ? 1 : "";
    return acc;
  }, {});

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        // ==========================================
        // VALIDATION
        // ==========================================

        // 1. Banner Image
        const bannerFile = Array.isArray(formData.banner)
          ? formData.banner[0]
          : formData.banner;

        const bannerImageError = validateBannerImage(bannerFile);

        if (bannerImageError !== true) {
          Swal.fire("Validation Error", bannerImageError, "warning");
          return;
        }

        // 2. Banner Title
        const bannerTitleError = validateBannerTitle(
          formData.banner_title
        );

        if (bannerTitleError !== true) {
          Swal.fire("Validation Error", bannerTitleError, "warning");
          return;
        }

        // 3. Banner Content
        const bannerContentError = validateBannerContent(
          formData.banner_content
        );

        if (bannerContentError !== true) {
          Swal.fire("Validation Error", bannerContentError, "warning");
          return;
        }

        // 4. Starting Price
        const startingPriceError = validateStartingPrice(
          formData.starting_price
        );

        if (startingPriceError !== true) {
          Swal.fire("Validation Error", startingPriceError, "warning");
          return;
        }

        // ==========================================
        // SUBMIT
        // ==========================================

        setLoading(true);

        console.log("Submitting formData:", formData);

        const formPayload = new FormData();

        allowedFields.forEach((field) => {
          if (field === "banner") {
            if (bannerFile) {
              formPayload.append("banner", bannerFile);
            }
          } else {
            formPayload.append(field, formData[field] ?? "");
          }
        });

        await createItem(formPayload);

        Swal.fire({
          title: "Success",
          text: "Banner created successfully!",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#164E4F",
        });

        navigate("/admin/sliders");
      } catch (error) {
        console.error("Create banner error:", error);

        const errorMessage =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors)
                .flat()
                .join(", ")
            : error.response?.data?.message ||
              "Failed to create banner";

        Swal.fire("Error", errorMessage, "error");
      } finally {
        setLoading(false);
      }
    },
    [createItem, navigate]
  );

  return (
    <DashboardWrapper>
      <div className="p-4">
        <h1 className="h4">Create Banner</h1>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            type="button"
            className="btn btn-secondary d-flex align-items-center justify-content-between"
            onClick={() => navigate(-1)}
          >
            <span>Back</span>
          </button>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <h5 className="card-header">Create Banner</h5>

              <div className="card-body demo-vertical-spacing demo-only-element">
                {!loading ? (
                  <CrudForm
                    initialData={initialData}
                    onSubmit={handleSubmit}
                    allowedFields={allowedFields}
                    fileFields={[
                      {
                        name: "banner",
                        multiple: false,
                      },
                    ]}
                  />
                ) : (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading...
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
